import React, { useState, useRef, useEffect, useContext } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { BrandContext } from '../context/BrandContext';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const MarkdownMessage = ({ content }) => (
  <div className="ai-markdown">
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
      p: ({children}) => <p className="my-1">{children}</p>,
      ul: ({children}) => <ul className="my-1 ml-4 list-disc">{children}</ul>,
      ol: ({children}) => <ol className="my-1 ml-4 list-decimal">{children}</ol>,
      li: ({children}) => <li className="my-0.5">{children}</li>,
      strong: ({children}) => <strong className="font-semibold">{children}</strong>,
      h3: ({children}) => <h3 className="text-sm font-bold mt-3 mb-1">{children}</h3>,
      h4: ({children}) => <h4 className="text-sm font-semibold mt-2 mb-1">{children}</h4>,
      table: ({children}) => <div className="overflow-x-auto my-2"><table className="min-w-full border-collapse text-xs">{children}</table></div>,
      thead: ({children}) => <thead className="bg-gray-50">{children}</thead>,
      th: ({children}) => <th className="border border-gray-200 px-2 py-1.5 font-semibold text-left text-gray-600">{children}</th>,
      td: ({children}) => <td className="border border-gray-200 px-2 py-1.5">{children}</td>,
    }}>{content}</ReactMarkdown>
  </div>
);

export default function ChatbotV2() {
  const { selectedBrand } = useContext(BrandContext) || {};
  const [sessions, setSessions] = useState([]); // Holds the list of conversations
  const [activeSessionId, setActiveSessionId] = useState(null); // The ID of the currently active conversation
  const [messages, setMessages] = useState([]);
  const [introDismissed, setIntroDismissed] = useState(false);
  const [introAnimating, setIntroAnimating] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const [typing, setTyping] = useState(false);
  const [deletingSessionId, setDeletingSessionId] = useState(null);
  const suggestions = [
    'Last 7-day summary',
    'Monthly breakdown',
    'Google vs Meta ROAS',
    'Top channel last 60 days'
  ];

  const sessionsAbortRef = useRef(null);
  const sessionsReqIdRef = useRef(0);

  const [userId] = useState(() => {
    const existing = localStorage.getItem('glowyAiUserId');
    if (existing) return existing;
    const id = uuidv4();
    localStorage.setItem('glowyAiUserId', id);
    return id;
  });

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending, typing]);

  useEffect(() => {
    setSessions([]);
    const existingBrandId = (() => {
      try {
        return localStorage.getItem('currentBrandId')
          || (selectedBrand && selectedBrand.id)
          || (() => { try { return JSON.parse(localStorage.getItem('selectedBrand') || '{}').id; } catch { return null; } })();
      } catch {
        return localStorage.getItem('currentBrandId') || (selectedBrand && selectedBrand.id) || null;
      }
    })();
    // Invalidate any in-flight before starting a new one
    sessionsReqIdRef.current += 1;
    // Swallow any stray rejections (e.g., StrictMode double invoke)
    loadSessions().catch(() => {});
    // Cleanup: abort on unmount or dependency change
    return () => {
      sessionsReqIdRef.current += 1; // invalidate pending responses
    };
  }, [selectedBrand]);

  // Swallow AbortError unhandled promise rejections to avoid noisy dev overlay
  useEffect(() => {
    const onUnhandledRejection = (event) => {
      try {
        const reason = event?.reason;
        const name = reason?.name || '';
        const msg = (reason?.message || String(reason || ''));
        const isAbort = name === 'AbortError' || /aborted/i.test(msg) || /signal is aborted/i.test(msg) || reason?.code === 20;
        if (isAbort) {
          event.preventDefault();
        }
      } catch (_e) {
        // ignore
      }
    };
    window.addEventListener('unhandledrejection', onUnhandledRejection);
    return () => window.removeEventListener('unhandledrejection', onUnhandledRejection);
  }, []);

  const getApiBaseUrl = () => {
    const isPlatformHost = (typeof window !== 'undefined') && /(^|\.)platform\.glowytics\.com$/i.test(window.location.hostname);
    return isPlatformHost
      ? 'https://api.glowytics.com'
      : (process.env.NODE_ENV === 'development' ? '' : 'https://api.glowytics.com');
  };

  const loadSessions = async () => {
    const brandId = localStorage.getItem('currentBrandId') 
      || (selectedBrand && selectedBrand.id) 
      || (() => { try { return JSON.parse(localStorage.getItem('selectedBrand') || '{}').id; } catch { return null; } })();
    const authToken = localStorage.getItem('token');
    console.log('loadSessions:init', { brandId, tokenExists: !!authToken, tokenLen: authToken ? authToken.length : 0 });
    if (!brandId) {
      console.warn('loadSessions: missing brandId');
      setSessions([]);
      return;
    }

    try {
      // mark new request
      const reqId = ++sessionsReqIdRef.current;
      const url = `${getApiBaseUrl()}/api/ai/conversations`;
      console.log('Loading conversations...', { url, brandId });
      const headers = { 'X-Brand-ID': brandId };
      if (authToken && authToken !== 'undefined' && authToken !== 'null') headers['Authorization'] = `Bearer ${authToken}`;
      const resp = await fetch(url, {
        headers,
        credentials: 'include'
      });

      console.log('Conversations response status:', resp.status);
      if (resp.status === 204) {
        if (reqId === sessionsReqIdRef.current) setSessions([]);
        return;
      }
      if (resp.ok) {
        const data = await resp.json();
        console.log('Conversations payload:', data);
        if (reqId === sessionsReqIdRef.current) setSessions(data.conversations || data.sessions || []);
      } else {
        const txt = await resp.text();
        console.warn('Failed to load conversations:', resp.status, txt);
        if (reqId === sessionsReqIdRef.current) setSessions([]);
      }
    } catch (e) {
      const msg = (e && (e.message || e.toString())) || '';
      const isAbort = e?.name === 'AbortError'
        || e?.code === 20
        || /aborted/i.test(msg)
        || /The user aborted a request/i.test(msg)
        || /signal is aborted/i.test(msg);
      if (isAbort) {
        // Silently ignore aborts to avoid dev overlay noise
        console.debug('Conversations request aborted');
        return;
      }
      console.warn('Failed to load conversations:', e);
      setSessions([]);
    }
  };

  const loadMessages = async (conversationId) => {
    const brandId = localStorage.getItem('currentBrandId') || (selectedBrand && selectedBrand.id);
    const authToken = localStorage.getItem('token');
    if (!brandId || !conversationId) return;

    try {
      let url = `${getApiBaseUrl()}/api/ai/conversations/${conversationId}`;
      console.log('Loading messages (primary)...', { url, conversationId });
      const headers = { 'X-Brand-ID': brandId };
      if (authToken && authToken !== 'undefined' && authToken !== 'null') headers['Authorization'] = `Bearer ${authToken}`;
      let resp = await fetch(url, {
        headers,
        credentials: 'include'
      });

      if (resp.ok) {
        const data = await resp.json();
        console.log('Messages payload (primary):', data);
        const formatted = (data.messages || []).map((m) => ({
          ...m,
          role: (m.sender_role ? m.sender_role.toLowerCase() : m.role || 'assistant')
        }));
        setMessages(formatted);
        return;
      }
      console.warn('Failed to load messages:', resp.status);
    } catch (e) {
      console.warn('Failed to load messages:', e);
    }
  };

  const createNewSession = () => {
    setActiveSessionId(null);
    setMessages([]);
  };
  
  const selectSession = async (id) => {
    setActiveSessionId(id);
    await loadMessages(id);
  };

  const handleDeleteSession = async (id) => {
    try {
      const brandId = localStorage.getItem('currentBrandId') 
        || (selectedBrand && selectedBrand.id) 
        || (() => { try { return JSON.parse(localStorage.getItem('selectedBrand') || '{}').id; } catch { return null; } })();
      if (!brandId || !id) return;
      setDeletingSessionId(id);
      const authToken = localStorage.getItem('token');
      const headers = { 'X-Brand-ID': brandId };
      if (authToken && authToken !== 'undefined' && authToken !== 'null') headers['Authorization'] = `Bearer ${authToken}`;
      const resp = await fetch(`${getApiBaseUrl()}/api/ai/conversations/${id}`, {
        method: 'DELETE',
        headers,
        credentials: 'include'
      });
      if (!resp.ok) {
        const txt = await resp.text().catch(() => '');
        console.warn('Delete conversation failed', resp.status, txt);
        return;
      }
      if (activeSessionId === id) {
        setActiveSessionId(null);
        setMessages([]);
      }
      await loadSessions();
    } catch (e) {
      console.warn('Delete conversation error:', e);
    } finally {
      setDeletingSessionId(null);
    }
  };

  const sendMessage = async (textOverride) => {
    const text = (textOverride ?? inputValue).trim();
    if (!text) return;

    // If intro hero is visible, animate it out before first send
    const introVisibleNow = (!introDismissed && !activeSessionId && messages.length === 0);
    if (introVisibleNow) {
      setIntroAnimating(true);
      setTimeout(() => {
        setIntroDismissed(true);
        setIntroAnimating(false);
      }, 650);
    }

    const brandId = localStorage.getItem('currentBrandId')
      || (selectedBrand && selectedBrand.id)
      || (() => { try { return JSON.parse(localStorage.getItem('selectedBrand') || '{}').id; } catch { return null; }})();
    const brandName = (() => { try {
      return (selectedBrand && selectedBrand.name)
        || (JSON.parse(localStorage.getItem('selectedBrand') || '{}').name)
        || localStorage.getItem('currentBrandName')
        || null;
    } catch { return (selectedBrand && selectedBrand.name) || null; } })();
    
    if (!brandId) {
      setMessages((prev) => [
        ...prev,
        { role: 'system', content: 'No brand selected. Please choose a brand first.' }
      ]);
      return;
    }

    const userMsg = { role: 'user', content: text };
    setMessages(prev => [...prev, userMsg]);
    setInputValue('');
    setSending(true);

    try {
      const authToken = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        'X-Brand-ID': brandId
      };
      if (authToken) headers['Authorization'] = `Bearer ${authToken}`;

      // Detect "Monthly Channel Revenue" intent and pass explicit hint to backend
      const lower = text.toLowerCase();
      const wantsChannelMonthly = /(monthly\s*channel\s*revenue|channel\s*monthly|kanal\s*(gelir|ciro).*ayl[ıi]k|ayl[ıi]k\s*kanal\s*(gelir|ciro)|kanal.*ayl[ıi]k.*(gelir|ciro)|monthly\s*breakdown\s*by\s*channel)/i.test(lower);
      const wantsMonthlyTrends = /(monthly\s*performance\s*trends|monthly\s*trend(s)?|ayl[ıi]k\s*performans(\s*trend(leri)?)?|ayl[ıi]k\s*kırılım|monthly\s*breakdown)/i.test(lower);
      const wantsWeekOverWeek = /(week-?over-?week|wow\s*summary|this\s*week\s*vs\s*last\s*week|hafta\s*hafta|bu\s*hafta.*geçen\s*hafta)/i.test(lower);
      const wantsWeekdayRevenue = /(weekday\s*revenue|revenue\s*by\s*weekday|günlere\s*göre\s*gelir|hafta\s*içi.*gelir|gün\s*bazlı\s*gelir)/i.test(lower);
      const wantsCampaignTypesOrNames = /(campaign\s*(types?|tipleri)|kampanya\s*tip(ler|i)|campaign\s*names?|kampanya\s*isim(ler|i)|campaign\s*list|kampanya\s*listesi)/i.test(lower);
      const wantsBudgetAlloc = /(bütçe\s*alokasyon(u)?|budget\s*allocation|bütçeyi\s*(artır|azalt)|bütçe\s*önerisi|budget\s*suggestion)/i.test(lower);
      // Turkish phrasing for active campaign inventory requests
      const wantsActiveCampaigns = /(aktif\s*kampanyalar(ımız)?|aktif\s*kampanyalar(ımız)?\s*nelerdir|aktif\s*kampanyaları\s*listele|kampanyalar(ımız)?\s*nelerdir|kampanya\s*isim(leri|lerini)\s*(listele|nedir|nelerdir)?|tüm\s*kampanyalar)/i.test(lower);
      const mentionsMeta = /(meta\s*ads?|facebook\s*ads?|meta)/i.test(lower);
      const mentionsGoogle = /(google\s*ads?|adwords|search|pmax|display|shopping)/i.test(lower);
      // Product performance intents (brand/category/product pivots and top products)
      const wantsTopProducts = /(top\s*10\s*(ürün|product)s?|en\s*çok\s*gelir\s*getiren\s*(ürün|product)ler|best\s*selling\s*products)/i.test(lower);
      const wantsProductDaily = /(product\s*performance|ürün\s*performans(ı)?|ürün\s*analitiği|product\s*daily|daily\s*product)/i.test(lower);
      const wantsBrandMonthly = /(brand\s*monthly|monthly\s*brand|marka\s*ayl[ıi]k|ayl[ıi]k\s*marka)/i.test(lower);
      const wantsCategoryMonthly = /(category\s*monthly|monthly\s*category|kategori\s*ayl[ıi]k|ayl[ıi]k\s*kategori)/i.test(lower);
      const wantsBrandWeekly = /(brand\s*weekly|weekly\s*brand|marka\s*haftal[ıi]k|haftal[ıi]k\s*marka)/i.test(lower);
      const wantsCategoryWeekly = /(category\s*weekly|weekly\s*category|kategori\s*haftal[ıi]k|haftal[ıi]k\s*kategori)/i.test(lower);

      const options = { narrative: true };
      if (wantsChannelMonthly) {
        options.includeChannelMonthlyRevenue = true;
        options.channelMonthlyDateRange = '180';
      }
      if (wantsMonthlyTrends) {
        options.includeMonthlyPerformanceTrends = true;
        options.monthlyTrendsDateRange = '365';
      }
      if (wantsWeekOverWeek) {
        options.includeWeekCompare = true;
      }
      if (wantsWeekdayRevenue) {
        // Use daily breakdown; backend aggregates per day which is suitable for weekday pivoting on UI
        options.breakdown = 'daily';
      }
      if (wantsCampaignTypesOrNames || wantsActiveCampaigns || wantsBudgetAlloc) {
        // Ask backend to include campaign type/name inventories derived from view_google_ads_campaigntype_all
        options.includeCampaignTypes = true;
        options.includeCampaignNames = true;
        // optional: allow custom span for inventory
        options.campaignInventoryDateRange = '180';
        if (mentionsMeta && !mentionsGoogle) options.campaignInventoryPlatform = 'meta';
        if (mentionsGoogle && !mentionsMeta) options.campaignInventoryPlatform = 'google';
      }
      // Map product performance asks to backend options
      if (wantsTopProducts) {
        options.includeTop10Products = true;
        options.productTop10DateRange = '30';
      }
      if (wantsProductDaily) {
        options.includeDailyProductPerformance = true;
      }
      if (wantsBrandMonthly) {
        options.includeMonthlyItemBrandSummary = true;
        options.itemBrandMonthlyDateRange = 'last12';
      }
      if (wantsCategoryMonthly) {
        options.includeMonthlyCategorySummary = true;
        options.categoryMonthlyDateRange = 'last12';
      }
      if (wantsBrandWeekly) {
        options.includeWeeklyItemBrandSummary = true;
        options.itemBrandWeeklyDateRange = 'last12';
      }
      if (wantsCategoryWeekly) {
        options.includeWeeklyCategoryPerformance = true;
        options.categoryWeeklyDateRange = 'last12';
      }

      const payload = {
        conversationId: activeSessionId, // Will be null for new chats
        message: text,
        options,
        userId,
        brandName
      };
      const intentHints = [];
      if (wantsChannelMonthly) intentHints.push('channel_monthly_revenue');
      if (wantsMonthlyTrends) intentHints.push('monthly_performance_trends');
      if (wantsWeekOverWeek) intentHints.push('weekly');
      if (wantsWeekdayRevenue) intentHints.push('daily_breakdown');
      if (wantsCampaignTypesOrNames || wantsActiveCampaigns || wantsBudgetAlloc) intentHints.push('campaign_inventory');
      if (wantsTopProducts) intentHints.push('product_top10');
      if (wantsProductDaily) intentHints.push('product_daily');
      if (wantsBrandMonthly) intentHints.push('brand_monthly');
      if (wantsCategoryMonthly) intentHints.push('category_monthly');
      if (wantsBrandWeekly) intentHints.push('brand_weekly');
      if (wantsCategoryWeekly) intentHints.push('category_weekly');
      if (intentHints.length) payload.intentHints = intentHints;

      const resp = await fetch(`${getApiBaseUrl()}/api/ai/chat/v2/respond`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (!resp.ok) {
        const errText = await resp.text();
        throw new Error(errText || `HTTP ${resp.status}`);
      }

      const data = await resp.json();

      if (data.conversationId && !activeSessionId) {
        setActiveSessionId(data.conversationId);
      }

      // Prefer dedicated campaign performance or category cards if provided
      if (data?.structured?.category_top_recent && Array.isArray(data.structured.category_top_recent.rows)) {
        try {
          const s = data.structured.category_top_recent;
          const catCard = {
            title: 'Top categories — last 30 days (today excluded)',
            period: s.period || {},
            rows: s.rows
              .map(r => ({ category: r.category, revenue: Math.round(Number(r.revenue||0)), purchases: Number(r.purchases||0), views: Number(r.views||0) }))
          };
          await appendAssistantTypewriter('', { categoryCard: catCard });
        } catch (_) {
          const assistantMessage = data?.assistantMessage || 'No response.';
          await appendAssistantTypewriter(assistantMessage);
        }
      } else if (data?.structured?.campaign_inventory_perf) {
        try {
          const perf = data.structured.campaign_inventory_perf;
          const makeRows = (arr) => (Array.isArray(arr) ? [...arr] : [])
            .sort((a,b)=> Number(b.cost||0) - Number(a.cost||0))
            .slice(0, 12)
            .map(r => ({
              name: String(r.name || ''),
              typeLabel: r.typeLabel,
              objectiveLabel: r.objectiveLabel,
              type: r.type,
              objective: r.objective,
              budget: Math.round(Number(r.budget || 0)),
              cost: Math.round(Number(r.cost || 0)),
              revenue: Math.round(Number(r.revenue || 0)),
              roas: (Number(r.cost||0) > 0 ? (Number(r.revenue||0)/Number(r.cost||0)) : 0),
              first5_roas: Number(r.first5_roas || 0),
              last4_roas: Number(r.last4_roas || 0),
              budget_reco: r.budget_reco
            }));
          const campaignPerfCard = {
            title: 'Active campaigns — last 9 days (today excluded)',
            period: perf.period || {},
            google: makeRows(perf.google),
            meta: makeRows(perf.meta),
            totals: perf.totals || {},
            groups: perf.groups || {},
            roas_trend: perf.roas_trend || null
          };
          await appendAssistantTypewriter('', { campaignPerfCard });
        } catch (_) {
          const assistantMessage = data?.assistantMessage || 'No response.';
          await appendAssistantTypewriter(assistantMessage);
        }
      } else if (data?.structured) {
        const s = data.structured;
        const userText = text;
        const isExplicitMetricsAsk = /\b(özet|summary|rapor|report|kpi|performans|performance|roas|top\s*10|top\s*\d+|ürün|product|kategori|category|kanal|channel|trend|breakdown|göster|tablo)\b/i.test(userText);
        const hasPeriod = !!(s?.period?.start && s?.period?.end);
        const numeric = (v) => Number.isFinite(Number(v)) && Number(v) !== 0;
        const hasAnyKpi = (
          numeric(s?.ga4?.sessions) || numeric(s?.ga4?.revenue) || numeric(s?.ga4?.purchases) ||
          numeric(s?.google?.cost) || numeric(s?.google?.revenue) || numeric(s?.google?.clicks) || numeric(s?.google?.purchases) ||
          numeric(s?.meta?.cost) || numeric(s?.meta?.revenue) || numeric(s?.meta?.clicks) || numeric(s?.meta?.purchases) ||
          (Array.isArray(s?.last6_months) && s.last6_months.length > 0)
        );
        if (!(isExplicitMetricsAsk && hasPeriod && hasAnyKpi)) {
          const assistantMessage = data?.assistantMessage || 'No response.';
          await appendAssistantTypewriter(assistantMessage);
        } else {
        const safeRound = (n) => Math.round(Number(n || 0));
        const kpiCard = {
          title: s?.last6_months?.length ? 'Last 6 months (today excluded)' : 'Last 7 days (today excluded)',
          period: { start: s?.period?.start, end: s?.period?.end },
          ga4: {
            sessions: safeRound(s?.ga4?.sessions),
            revenue: safeRound(s?.ga4?.revenue),
            purchases: safeRound(s?.ga4?.purchases || 0),
            cr: (() => {
              const sess = Number(s?.ga4?.sessions || 0);
              const pur = Number(s?.ga4?.purchases || 0);
              return sess > 0 ? Math.round((pur / sess) * 1000) / 10 : 0; // % with 1 decimal
            })()
          },
          google: {
            cost: safeRound(s?.google?.cost),
            revenue: safeRound(s?.google?.revenue),
            purchases: safeRound(s?.google?.purchases || 0),
            clicks: safeRound(s?.google?.clicks || 0)
          },
          meta: {
            cost: safeRound(s?.meta?.cost),
            revenue: safeRound(s?.meta?.revenue),
            purchases: safeRound(s?.meta?.purchases || 0),
            clicks: safeRound(s?.meta?.clicks || 0)
          },
          last6Months: Array.isArray(s?.last6_months) ? s.last6_months : []
        };
        await appendAssistantTypewriter('', { kpiCard });
        }
      } else {
        // Handle persisted cards via markers
        const markerTop10 = '__KPI_CARD_TOP10_PRODUCTS_V1__';
        if (typeof data?.assistantMessage === 'string' && data.assistantMessage.startsWith(markerTop10)) {
          try {
            const parsed = JSON.parse(data.assistantMessage.replace(markerTop10, ''));
            const rows = Array.isArray(parsed.rows) ? parsed.rows : [];
            const card = {
              title: 'Top 10 Products — recent period',
              period: parsed.period || {},
              rows
            };
            await appendAssistantTypewriter('', { top10Card: card });
          } catch (_) {
            const assistantMessage = data?.assistantMessage || 'No response.';
            await appendAssistantTypewriter(assistantMessage);
          }
        } else {
          const assistantMessage = data?.assistantMessage || 'No response.';
          await appendAssistantTypewriter(assistantMessage);
        }
      }
      
      // Refresh session list to show new message preview and timestamp
      await loadSessions();

    } catch (e) {
      setMessages((prev) => [...prev, { role: 'system', content: `Error: ${e.message}` }]);
    } finally {
      setSending(false);
    }
  };

  const appendAssistantTypewriter = (fullText, extras = {}) => new Promise((resolve) => {
    const TYPE_INTERVAL = 16;
    const CHARS_PER_TICK = 2;
    setTyping(true);
    let targetIndex = -1;
    setMessages((prev) => {
      targetIndex = prev.length;
      return [...prev, { role: 'assistant', content: '', ...extras }];
    });
    let i = 0;
    const interval = setInterval(() => {
      i = Math.min(fullText.length, i + CHARS_PER_TICK);
      const slice = fullText.slice(0, i);
      setMessages((prev) => prev.map((m, idx) => (idx === targetIndex ? { ...m, content: slice } : m)));
      if (i >= fullText.length) {
        clearInterval(interval);
        setTyping(false);
        resolve();
      }
    }, TYPE_INTERVAL);
  });

  const handleQuick = (mode) => {
    // Intent mode removed - not used
    if (mode === 'weekly') sendMessage('Show weekly performance summary');
    else if (mode === 'monthly') sendMessage('Show monthly performance summary');
    else sendMessage('Show daily performance');
  };

  return (
    <div className="w-full h-[calc(100vh-80px)] p-4 flex flex-col gap-4 min-h-0">
      {/* Removed standalone header card; branding moved inside chat card */}
      {/* Layout: main chat + right sidebar */}
      <div className="flex-1 grid grid-cols-12 gap-4 min-h-0">
        {/* Chat area (messages + input in one card) */}
        <div className="col-span-12 lg:col-span-10 h-full min-h-0">
          <div className="h-full min-h-0 bg-white rounded-2xl shadow-md border border-gray-300 flex flex-col">
            <div className="p-4 pb-3 border-b border-gray-300 bg-gradient-to-r from-white to-[#f8fafc] rounded-t-2xl relative overflow-hidden">
              <div className="absolute inset-0 pointer-events-none opacity-60" style={{backgroundImage: 'radial-gradient(40% 60% at 10% -10%, rgba(124,58,237,0.10), transparent 60%), radial-gradient(50% 70% at 90% -10%, rgba(37,99,235,0.08), transparent 60%)'}} />
              <div className="relative z-10 flex items-center gap-3">
                <div className="h-10 w-10 rounded-full overflow-hidden ring-2 ring-violet-200 shadow-sm bg-white">
                  <img src="/GlowyAIv3.png" alt="GlowyAI" className="h-full w-full object-cover" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h1 className="text-lg font-semibold text-gray-900 tracking-[-0.01em]">GlowyAI</h1>
                    <span className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-gray-200 bg-white text-[10px] text-gray-600">
                      <img src="/chatgpt.png" alt="OpenAI" className="h-3.5 w-3.5 object-contain" />
                      Powered by OpenAI
                    </span>
                  </div>
                  <p className="text-xs text-gray-500">Your AI marketing co‑pilot</p>
                </div>
                <div className="hidden lg:flex items-center gap-2 text-xs text-gray-500">
                  <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-emerald-500"/>Online</span>
                </div>
              </div>
              <div className="relative z-10 mt-3 flex flex-wrap gap-2">
                {suggestions.map((s) => (
                  <button key={s} onClick={() => sendMessage(s)} className="px-2.5 py-1 text-xs rounded-full border border-gray-200 bg-white hover:bg-gray-50 text-gray-700">
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 relative">
              <div className="space-y-3">
                {messages.map((m, idx) => {
                  const isSystem = m.role === 'system';
                  const isAssistant = !isSystem && m.role !== 'user';
                  const bubbleClass = isSystem
                    ? 'bg-amber-50 text-amber-900 border border-amber-300 shadow-sm'
                    : 'bg-gray-100 text-gray-900 border border-gray-300 shadow-sm';
                  return (
                    <div key={idx} className={`flex ${m.role==='user'?'justify-end':'justify-start'}`}>
                      {isAssistant ? (
                        <div className="flex items-start gap-2 max-w-[80%]">
                          <div className="h-11 w-11 rounded-full overflow-hidden ring-1 ring-violet-200 flex-shrink-0 mt-0.5 bg-white">
                            <img src="/GlowyAIv3.png" alt="GlowyAI" className="h-full w-full object-cover" />
                          </div>
                          <div className={`${bubbleClass} px-3 py-2 rounded-xl text-sm leading-5`}>
                            {/* Optional compact KPI card rendering */}
                            {m.categoryCard ? (
                              (() => {
                                const c = m.categoryCard;
                                return (
                                  <div className="text-[13px] leading-5">
                                    <div className="text-xs text-gray-500 mb-2">{c.title}</div>
                                    <div className="overflow-x-auto">
                                          <table className="text-[12px] text-gray-800 min-w-[720px]">
                                        <thead>
                                          <tr className="text-gray-500">
                                            <th className="pr-4 text-left">Category</th>
                                            <th className="pr-4 text-right">Revenue</th>
                                            <th className="pr-4 text-right">Purchases</th>
                                                <th className="pr-4 text-right">Views</th>
                                                <th className="pr-4 text-right">Add to Cart</th>
                                                <th className="pr-4 text-right">RPV</th>
                                                <th className="pr-0 text-right">CR</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {c.rows.map((r, i) => (
                                            <tr key={i} className="border-t border-gray-100">
                                              <td className="pr-4">{r.category}</td>
                                              <td className="pr-4 text-right">₺{Number(r.revenue||0).toLocaleString()}</td>
                                              <td className="pr-4 text-right">{Number(r.purchases||0).toLocaleString()}</td>
                                                  <td className="pr-4 text-right">{Number(r.views||0).toLocaleString()}</td>
                                                  <td className="pr-4 text-right">{Number(r.add_to_cart||0).toLocaleString()}</td>
                                                  <td className="pr-4 text-right">{(() => { const v=Number(r.views||0), rev=Number(r.revenue||0), rpv = ('rpv' in r && r.rpv!=null) ? Number(r.rpv) : (v>0 ? (rev/v) : 0); return '₺'+rpv.toFixed(2); })()}</td>
                                                  <td className="pr-0 text-right">{(() => { const v=Number(r.views||0), p=Number(r.purchases||0); return v>0 ? ((p/v)*100).toFixed(2)+'%' : '0.00%'; })()}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                    <div className="text-xs text-gray-500 mt-2">Period {c.period?.start} → {c.period?.end}</div>
                                  </div>
                                );
                              })()
                            ) : m.campaignPerfCard || (typeof m.content === 'string' && m.content.startsWith('__KPI_CARD_CAMPAIGN_V1__')) ? (
                              (() => {
                                const card = m.campaignPerfCard || (() => { try {
                                  const parsed = JSON.parse(m.content.replace('__KPI_CARD_CAMPAIGN_V1__',''));
                                  return {
                                    title: 'Active campaigns — last 9 days (today excluded)',
                                    period: parsed.period,
                                    google: parsed.google,
                                    meta: parsed.meta,
                                    totals: parsed.totals,
                                    groups: parsed.groups,
                                    roas_trend: parsed.roas_trend
                                  };
                                } catch { return null; } })();
                                if (!card) return m.content;
                                m.campaignPerfCard = card; // memoize
                                return (
                                  <div className="text-[13px] leading-5">
                                    <div className="text-xs text-gray-500 mb-2">{card.title}</div>
                                    {/* Removed top-level trend display as requested */}
                                    {[{key:'google', label:'Google Ads', icon:'/google_ads-512.webp'}, {key:'meta', label:'Meta Ads', icon:'/6033716.png'}].map(p => (
                                      Array.isArray(card[p.key]) && card[p.key].length > 0 ? (
                                        <div key={p.key} className="w-full pt-2 border-t border-gray-200">
                                          <div className="flex items-center gap-2 mb-1"><img src={p.icon} className="h-4 w-4" alt={p.label} /><span className="text-xs text-gray-600">{p.label}</span></div>
                                          {(() => {
                                            const allRows = card[p.key];
                                            const groupsFromPayload = p.key === 'google' ? (card.groups?.googleByType || []) : (card.groups?.metaByObjective || []);
                                            const fallbackLabels = Array.from(new Set(allRows.map(r => p.key==='google' ? (r.typeLabel||r.type||'Unknown') : (r.objectiveLabel||r.objective||'Unknown')))).map(label => ({ label }));
                                            const groupList = (groupsFromPayload.length ? groupsFromPayload : fallbackLabels).sort((a,b)=> Number(b.cost||0) - Number(a.cost||0));
                                            // Build a single aligned table with group header rows and campaign rows under each
                                            const buildRows = [];
                                            groupList.forEach((gr) => {
                                              const label = gr.label;
                                              const rows = allRows.filter(r => (p.key==='google' ? (r.typeLabel||r.type||'Unknown') : (r.objectiveLabel||r.objective||'Unknown')) === label);
                                              const sum = (k) => rows.reduce((acc,x)=>acc+Number(x[k]||0),0);
                                              const totCost = gr.cost!=null ? gr.cost : sum('cost');
                                              const totRev = gr.revenue!=null ? gr.revenue : sum('revenue');
                                              const totRoas = totCost>0 ? (totRev/totCost) : 0;
                                              buildRows.push({ kind: 'group', label, cost: totCost, revenue: totRev, roas: totRoas });
                                              rows.forEach(r => buildRows.push({ kind: 'item', ...r }));
                                            });
                                            return (
                                              <div className="overflow-x-auto">
                            <table className="text-[12px] text-gray-800 min-w-[920px]">
                                                  <thead>
                                                    <tr className="text-gray-500">
                                                      <th className="pr-4 text-left">{p.key==='google' ? 'Type / Campaign' : 'Objective / Campaign'}</th>
                                                      <th className="pr-4 text-right">Daily Budget</th>
                                                      <th className="pr-4 text-right">Cost</th>
                                                      <th className="pr-4 text-right">Revenue</th>
                                                      <th className="pr-4 text-right">ROAS</th>
                                                      <th className="pr-4 text-right">First 5 ROAS</th>
                                  <th className="pr-4 text-right">Last 4 ROAS</th>
                                  <th className="pr-0 text-right">Budget Suggestion</th>
                                                    </tr>
                                                  </thead>
                                                  <tbody>
                                                    {buildRows.map((r, i) => r.kind==='group' ? (
                                                      <tr key={i} className="border-t border-gray-200 bg-gray-50">
                                                        <td className="pr-4 font-medium text-gray-700">{r.label}</td>
                                                        <td className="pr-4 text-right font-medium text-gray-700">—</td>
                                                        <td className="pr-4 text-right font-medium text-gray-700">₺{Number(r.cost||0).toLocaleString()}</td>
                                                        <td className="pr-4 text-right font-medium text-gray-700">₺{Number(r.revenue||0).toLocaleString()}</td>
                                                        <td className="pr-4 text-right font-medium text-gray-700">{Number(r.roas||0).toFixed(2)}x</td>
                                                        <td className="pr-4 text-right font-medium text-gray-700">—</td>
                                    <td className="pr-4 text-right font-medium text-gray-700">—</td>
                                    <td className="pr-0 text-right font-medium text-gray-700">—</td>
                                                      </tr>
                                                    ) : (
                                                      <tr key={i} className="border-t border-gray-100">
                                                        <td className="pr-4">{r.name}</td>
                                                        <td className="pr-4 text-right">₺{Number(r.budget||0).toLocaleString()}</td>
                                                        <td className="pr-4 text-right">₺{Number(r.cost||0).toLocaleString()}</td>
                                                        <td className="pr-4 text-right">₺{Number(r.revenue||0).toLocaleString()}</td>
                                                        <td className="pr-4 text-right">{Number(r.roas||0).toFixed(2)}x</td>
                                                        <td className="pr-4 text-right">{Number(r.first5_roas||0).toFixed(2)}x</td>
                                    <td className="pr-4 text-right">{Number(r.last4_roas||0).toFixed(2)}x</td>
                                    <td className="pr-0 text-right">{(() => { const reco=(r.budget_reco||'').toLowerCase(); return reco==='increase' ? 'Increase' : reco==='decrease' ? 'Decrease' : 'Hold'; })()}</td>
                                                      </tr>
                                                    ))}
                                                    <tr className="border-t border-gray-300 font-semibold">
                                                      <td className="pr-4 text-left text-gray-700">Total</td>
                                                      <td className="pr-4 text-right text-gray-900">—</td>
                                                      <td className="pr-4 text-right text-gray-900">₺{Number(card?.totals?.[p.key]?.cost||0).toLocaleString()}</td>
                                                      <td className="pr-4 text-right text-gray-900">₺{Number(card?.totals?.[p.key]?.revenue||0).toLocaleString()}</td>
                                                      <td className="pr-4 text-right text-gray-900">{Number(card?.totals?.[p.key]?.roas||0).toFixed(2)}x</td>
                                                      <td className="pr-4 text-right text-gray-900">—</td>
                                  <td className="pr-4 text-right text-gray-900">—</td>
                                  <td className="pr-0 text-right text-gray-900">—</td>
                                                    </tr>
                                                  </tbody>
                                                </table>
                                              </div>
                                            );
                                          })()}
                                        </div>
                                      ) : null
                                    ))}
                                    <div className="text-xs text-gray-500 mt-2">Period {card.period?.start} → {card.period?.end}</div>
                                  </div>
                                );
                              })()
                            ) : m.kpiCard || (typeof m.content === 'string' && m.content.startsWith('__KPI_CARD_V1__')) ? (
                              (() => {
                                const card = m.kpiCard || (() => { try {
                                  const parsed = JSON.parse(m.content.replace('__KPI_CARD_V1__',''));
                                  // Normalize legacy payloads that used last6_months key
                                  if (!parsed.last6Months && Array.isArray(parsed.last6_months)) {
                                    parsed.last6Months = parsed.last6_months;
                                  }
                                  return parsed;
                                } catch { return null; } })();
                                if (!card) return m.content;
                                m.kpiCard = card; // memoize for rerenders
                                return (
                              <div className="text-[13px] leading-5">
                                <div className="text-xs text-gray-500 mb-2">{m.kpiCard.title}</div>
                                <div className="space-y-3">
                                  {/* GA4 group */}
                                  <div className="w-full">
                                    <div className="flex items-center gap-2 mb-1"><img src="/GA4logoicon.svg" className="h-4 w-4" alt="GA4" /><span className="text-xs text-gray-600">GA4</span></div>
                                    <div className="grid grid-cols-4 gap-2">
                                      <div>
                                        <div className="text-gray-600">Sessions</div>
                                        <div className="font-semibold text-gray-900">{Number(m?.kpiCard?.ga4?.sessions || 0).toLocaleString()}</div>
                                      </div>
                                      <div>
                                        <div className="text-gray-600">Revenue</div>
                                        <div className="font-semibold text-gray-900">₺{Number(m?.kpiCard?.ga4?.revenue || 0).toLocaleString()}</div>
                                      </div>
                                      <div>
                                        <div className="text-gray-600">Purchases</div>
                                        <div className="font-semibold text-gray-900">{Number(m?.kpiCard?.ga4?.purchases || 0).toLocaleString()}</div>
                                      </div>
                                      <div>
                                        <div className="text-gray-600">Conversion Rate</div>
                                        <div className="font-semibold text-gray-900">{Number(m?.kpiCard?.ga4?.cr || 0).toFixed(2)}%</div>
                                      </div>
                                    </div>
                                  </div>
                                  {/* Google group */}
                                  <div className="w-full pt-2 border-t border-gray-200">
                                    <div className="flex items-center gap-2 mb-1"><img src="/google_ads-512.webp" className="h-4 w-4" alt="Google" /><span className="text-xs text-gray-600">Google Ads</span></div>
                                    <div className="grid grid-cols-4 gap-2">
                                      <div>
                                        <div className="text-gray-600">Cost</div>
                                        <div className="font-semibold text-gray-900">₺{Number(m?.kpiCard?.google?.cost || 0).toLocaleString()}</div>
                                      </div>
                                      <div>
                                        <div className="text-gray-600">Revenue</div>
                                        <div className="font-semibold text-gray-900">₺{Number(m?.kpiCard?.google?.revenue || 0).toLocaleString()}</div>
                                      </div>
                                      <div>
                                      <div className="text-gray-600">Conversions</div>
                                        <div className="font-semibold text-gray-900">{Number(m?.kpiCard?.google?.purchases || 0).toLocaleString()}</div>
                                      </div>
                                      <div>
                                        <div className="text-gray-600">Conversion Rate</div>
                                        <div className="font-semibold text-gray-900">{(() => { const clicks = Number(m?.kpiCard?.google?.clicks || 0); const conv = Number(m?.kpiCard?.google?.purchases || 0); return clicks>0? ((conv/clicks)*100).toFixed(2): '0.00'; })()}%</div>
                                      </div>
                                    </div>
                                  </div>
                                  {/* Meta group */}
                                  <div className="w-full pt-2 border-t border-gray-200">
                                    <div className="flex items-center gap-2 mb-1"><img src="/6033716.png" className="h-4 w-4" alt="Meta" /><span className="text-xs text-gray-600">Meta Ads</span></div>
                                    <div className="grid grid-cols-4 gap-2">
                                      <div>
                                        <div className="text-gray-600">Cost</div>
                                        <div className="font-semibold text-gray-900">₺{Number(m?.kpiCard?.meta?.cost || 0).toLocaleString()}</div>
                                      </div>
                                      <div>
                                        <div className="text-gray-600">Revenue</div>
                                        <div className="font-semibold text-gray-900">₺{Number(m?.kpiCard?.meta?.revenue || 0).toLocaleString()}</div>
                                      </div>
                                      <div>
                                      <div className="text-gray-600">Conversions</div>
                                        <div className="font-semibold text-gray-900">{Number(m?.kpiCard?.meta?.purchases || 0).toLocaleString()}</div>
                                      </div>
                                      <div>
                                        <div className="text-gray-600">Conversion Rate</div>
                                        <div className="font-semibold text-gray-900">{(() => { const clicks = Number(m?.kpiCard?.meta?.clicks || 0); const conv = Number(m?.kpiCard?.meta?.purchases || 0); return clicks>0? ((conv/clicks)*100).toFixed(2): '0.00'; })()}%</div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                {Array.isArray(m?.kpiCard?.last6Months) && m.kpiCard.last6Months.length > 0 && (
                                  <div className="mt-3 pt-3 border-t border-gray-200">
                                    <div className="text-xs text-gray-600 mb-1">Monthly breakdown</div>
                                    <div className="overflow-x-auto">
                                      <table className="text-[12px] text-gray-800">
                                        <thead>
                                          <tr className="text-gray-500">
                                            <th className="pr-4 text-left">Month</th>
                                            <th className="pr-4 text-right">GA4 Revenue</th>
                                            <th className="pr-4 text-right">GA4 Purchases</th>
                                            <th className="pr-4 text-right">GA4 Sessions</th>
                                            <th className="pr-6 text-right">GA4 CR</th>
                                            <th className="pr-4 text-right">Google Cost</th>
                                            <th className="pr-6 text-right">Google Revenue</th>
                                            <th className="pr-4 text-right">Meta Cost</th>
                                            <th className="pr-0 text-right">Meta Revenue</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {(() => {
                                            try {
                                              const rows = [...m.kpiCard.last6Months].sort((a,b) => {
                                                const da = new Date((a?.month||'')+"-01T00:00:00Z").getTime();
                                                const db = new Date((b?.month||'')+"-01T00:00:00Z").getTime();
                                                return db - da; // newest first
                                              });
                                              return rows.map((row, i) => (
                                                <tr key={i} className="border-t border-gray-100">
                                                  <td className="pr-4">{(() => { try { const d=new Date(row.month+'-01T00:00:00Z'); return d.toLocaleString('en-US',{month:'long',year:'numeric'});} catch { return row.month; }})()}</td>
                                                  <td className="pr-4 text-right">₺{Math.round(Number(row.ga4?.revenue || 0)).toLocaleString()}</td>
                                                  <td className="pr-4 text-right">{Math.round(Number(row.ga4?.purchases || 0)).toLocaleString()}</td>
                                                  <td className="pr-4 text-right">{Math.round(Number(row.ga4?.sessions || 0)).toLocaleString()}</td>
                                                  <td className="pr-6 text-right">{(() => { const sess = Number(row.ga4?.sessions || 0); const pur = Number(row.ga4?.purchases || 0); return sess>0 ? ((pur/sess)*100).toFixed(2)+'%' : '0.00%'; })()}</td>
                                                  <td className="pr-4 text-right">₺{Math.round(Number(row.google?.cost || 0)).toLocaleString()}</td>
                                                  <td className="pr-6 text-right">₺{Math.round(Number(row.google?.revenue || 0)).toLocaleString()}</td>
                                                  <td className="pr-4 text-right">₺{Math.round(Number(row.meta?.cost || 0)).toLocaleString()}</td>
                                                  <td className="pr-0 text-right">₺{Math.round(Number(row.meta?.revenue || 0)).toLocaleString()}</td>
                                                </tr>
                                              ));
                                            } catch { return null; }
                                          })()}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                )}
                                <div className="text-xs text-gray-500 mt-2">Period {m.kpiCard.period.start} → {m.kpiCard.period.end}</div>
                              </div>
                                );
                              })()
                            ) : (
                              m.top10Card ? ( (() => { const c=m.top10Card; return (
                                <div className="text-[13px] leading-5">
                                  <div className="text-xs text-gray-500 mb-2">Top 10 Products — recent period</div>
                                  <div className="overflow-x-auto">
                                    <table className="text-[12px] text-gray-800 min-w-[540px]">
                                      <thead>
                                        <tr className="text-gray-500">
                                          <th className="pr-4 text-left">Product</th>
                                          <th className="pr-4 text-right">Revenue</th>
                                          <th className="pr-0 text-right">Purchases</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {c.rows.map((r,i)=> (
                                          <tr key={i} className="border-t border-gray-100"><td className="pr-4">{r.name}</td><td className="pr-4 text-right">₺{Number(r.revenue||0).toLocaleString()}</td><td className="pr-0 text-right">{Number(r.purchases||0).toLocaleString()}</td></tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                  <div className="text-xs text-gray-500 mt-2">Period {c.period?.start} → {c.period?.end}</div>
                                </div>
                              ); })() ) : <MarkdownMessage content={m.content} />
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className={`${bubbleClass} max-w-[75%] px-3 py-2 rounded-xl whitespace-pre-wrap text-sm leading-5`}>
                          {m.content}
                        </div>
                      )}
                    </div>
                  );
                })}
                {sending && (
                  <div className="flex justify-start">
                    <div className="bg-transparent px-3 py-2 rounded-xl text-sm">
                      <span className="relative flex h-3 w-3">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-violet-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-violet-500"></span>
                      </span>
                    </div>
                  </div>
                )}
              </div>
              {(() => {
                const showIntro = (!introDismissed && !activeSessionId && messages.length === 0) || introAnimating;
                if (!showIntro) return null;
                return (
                  <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${introAnimating ? 'opacity-0' : 'opacity-100'}`}>
                    <div className="text-center max-w-lg mx-auto">
                      <div className="relative mx-auto mb-4 h-40 w-40">
                        <div className="absolute inset-0 rounded-full bg-[conic-gradient(#a78bfa_0deg,#f0abfc_120deg,#60a5fa_240deg,#a78bfa_360deg)] animate-[spin_5s_linear_infinite] shadow-[0_0_28px_rgba(167,139,250,.55)]"></div>
                        <div className="absolute inset-[6px] rounded-full bg-white overflow-hidden">
                          <img src="/GlowyAIv3.png" alt="GlowyAI" className="h-full w-full object-cover" />
                        </div>
                      </div>
                      <h2 className="text-base font-semibold text-gray-900">Hi! I’m GlowyAI</h2>
                      <p className="text-sm text-gray-500 mt-1">Ask for highlights, ROAS compares, top channels, or product insights.</p>
                      <div className="mt-4 flex flex-wrap gap-2 justify-center">
                        {suggestions.map((s) => (
                          <button key={s} onClick={() => sendMessage(s)} className="px-3 py-1.5 text-xs rounded-full border border-gray-200 bg-white hover:bg-gray-50 text-gray-700">
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
            <form
              onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
              className="border-t border-gray-300 p-3 flex items-center gap-2"
            >
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                placeholder="Type your message…"
                className="flex-1 px-4 py-2 border border-gray-200 rounded-full bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white"
                disabled={sending}
              />
              <button
                type="submit"
                disabled={sending}
                className="px-3 py-2 rounded-full border border-gray-200 bg-white text-gray-700 hover:bg-gray-100 active:bg-gray-200 disabled:opacity-50 flex items-center justify-center transition-transform hover:scale-[0.98]"
                aria-label="Send"
                title="Send"
              >
                <img src="/send.png" alt="Send" className="h-5 w-5 object-contain" />
              </button>
            </form>
          </div>
        </div>
        {/* Sidebar (fixed height card with internal scroll) */}
        <aside className="col-span-12 lg:col-span-2 h-full min-h-0">
          <div className="h-full min-h-0 bg-white rounded-2xl shadow-md border border-gray-300 flex flex-col">
            <div className="flex items-center justify-between p-3 border-b border-gray-300">
              <h2 className="text-sm font-semibold text-gray-700">Chats</h2>
              <button
                onClick={createNewSession}
                className="p-1 rounded-md hover:bg-gray-100"
                aria-label="New Chat"
                title="New Chat"
              >
                <img src="/post.png" alt="New Chat" className="h-5 w-5 object-contain" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-1">
              {sessions.length === 0 && (
                <div className="text-xs text-gray-500 text-center py-4">No chats yet</div>
              )}
              {sessions.map((s) => (
                <div
                  key={s.id}
                  onClick={() => selectSession(s.id)}
                  className={`w-full px-2 py-2 rounded-lg border relative cursor-pointer ${activeSessionId===s.id? 'bg-gray-50 border-gray-300':'bg-white border-transparent hover:bg-gray-50'}`}
                >
                  {activeSessionId===s.id && <span className="absolute left-0 top-0 h-full w-1 bg-violet-500 rounded-l-lg" />}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-800 truncate">{s.title || 'Untitled'}</div>
                      {s.preview && (
                        <div className="text-xs text-gray-500 truncate mt-0.5">{s.preview}</div>
                      )}
                      <div className="text-xs text-gray-400 mt-1 flex items-center justify-between">
                        <span>{s.message_count || 0} messages</span>
                        <span>{s.updated_at ? new Date(s.updated_at).toLocaleDateString('tr-TR', {month: 'short', day: 'numeric'}) : ''}</span>
                      </div>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteSession(s.id); }}
                      className={`p-1 rounded-md border ${deletingSessionId===s.id ? 'bg-gray-100 text-gray-400 border-gray-200' : 'bg-white text-gray-500 border-gray-200 hover:bg-red-50 hover:text-red-600'}`}
                      title="Delete chat"
                      aria-label="Delete chat"
                      disabled={deletingSessionId === s.id}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M6 7h12M9 7V5a2 2 0 012-2h2a2 2 0 012 2v2" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </aside>
      </div>

      {/* Structured summary UI removed */}

      {/* input moved inside chat card */}
    </div>
  );
}


